import { useState } from 'react';
import {
  type ColumnDef,
  type SortingState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { ArrowUpDown, ChevronLeft, ChevronRight, Download, Search } from 'lucide-react';
import { exportToXlsx } from '@/lib/export-xlsx';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

interface DataTableProps<TData> {
  columns: ColumnDef<TData, unknown>[];
  data: TData[];
  /** Placeholder del buscador global. Si se omite, no se muestra el buscador. */
  searchPlaceholder?: string;
  /** Nombre del archivo `.xlsx` (sin extensión). Si se omite, no hay botón de export. */
  exportFileName?: string;
  /**
   * Mapea las filas (ya filtradas) a objetos planos para el Excel. Las claves
   * son los encabezados de columna del .xlsx. Si se omite, no hay export.
   */
  exportRows?: (rows: TData[]) => Record<string, unknown>[];
  emptyMessage?: string;
  pageSize?: number;
  /** Acciones extra a la derecha de la toolbar (ej. botón "Nuevo"). */
  toolbarActions?: React.ReactNode;
  /** Si se pasa, la fila es clickeable (cursor + onClick). Los controles dentro
   * de una celda deben hacer stopPropagation para no disparar esto. */
  onRowClick?: (row: TData) => void;
}

/**
 * DataTable reutilizable (TanStack Table + shadcn). Búsqueda global, orden por
 * columna, paginación y export a Excel (.xlsx vía SheetJS). El export respeta
 * el filtro de búsqueda activo (exporta lo filtrado, no solo la página).
 */
export function DataTable<TData>({
  columns,
  data,
  searchPlaceholder,
  exportFileName,
  exportRows,
  emptyMessage = 'Sin resultados.',
  pageSize = 10,
  toolbarActions,
  onRowClick,
}: DataTableProps<TData>) {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [globalFilter, setGlobalFilter] = useState('');

  const table = useReactTable({
    data,
    columns,
    state: { sorting, globalFilter },
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: { pagination: { pageSize } },
  });

  function handleExport() {
    if (!exportRows || !exportFileName) return;
    const filtered = table.getFilteredRowModel().rows.map((r) => r.original);
    exportToXlsx(exportFileName, exportRows(filtered));
  }

  const showSearch = Boolean(searchPlaceholder);
  const showExport = Boolean(exportRows && exportFileName);
  const totalRows = table.getFilteredRowModel().rows.length;
  const pageCount = table.getPageCount();

  return (
    <div className="space-y-3">
      {(showSearch || showExport || toolbarActions) && (
        <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
          {showSearch && (
            <div className="relative w-full sm:max-w-sm sm:flex-1 sm:min-w-[180px]">
              <Search className="absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={globalFilter}
                onChange={(e) => setGlobalFilter(e.target.value)}
                placeholder={searchPlaceholder}
                className="pl-8"
              />
            </div>
          )}
          <div className="flex flex-wrap items-center gap-2 sm:ml-auto [&>*]:flex-1 sm:[&>*]:flex-none">
            {toolbarActions}
            {showExport && (
              <Button variant="outline" size="sm" onClick={handleExport} disabled={totalRows === 0}>
                <Download />
                Exportar
              </Button>
            )}
          </div>
        </div>
      )}

      <div className="overflow-hidden rounded-lg border border-border">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((hg) => (
              <TableRow key={hg.id}>
                {hg.headers.map((header) => {
                  const canSort = header.column.getCanSort();
                  return (
                    <TableHead key={header.id}>
                      {header.isPlaceholder ? null : canSort ? (
                        <button
                          type="button"
                          onClick={header.column.getToggleSortingHandler()}
                          className="inline-flex items-center gap-1 hover:text-foreground"
                        >
                          {flexRender(header.column.columnDef.header, header.getContext())}
                          <ArrowUpDown className="size-3.5 opacity-50" />
                        </button>
                      ) : (
                        flexRender(header.column.columnDef.header, header.getContext())
                      )}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length} className="h-24 text-center text-muted-foreground">
                  {emptyMessage}
                </TableCell>
              </TableRow>
            ) : (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  onClick={onRowClick ? () => onRowClick(row.original) : undefined}
                  className={onRowClick ? 'cursor-pointer' : undefined}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {pageCount > 1 && (
        <div className="flex items-center justify-between gap-2 text-sm text-muted-foreground">
          <span>
            {totalRows} fila{totalRows === 1 ? '' : 's'}
          </span>
          <div className="flex items-center gap-2">
            <span className={cn('tabular-nums')}>
              Pág. {table.getState().pagination.pageIndex + 1} de {pageCount}
            </span>
            <Button
              variant="outline"
              size="icon"
              className="size-8"
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
            >
              <ChevronLeft className="size-4" />
            </Button>
            <Button
              variant="outline"
              size="icon"
              className="size-8"
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}
            >
              <ChevronRight className="size-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
